/**
 * Assinatura eletrônica — o formato do que o servidor devolve nas duas frentes: a tela interna
 * (documentos da empresa) e a página pública do signatário.
 */

export type StatusDoDocumento = 'pendente' | 'assinado' | 'recusado' | 'cancelado' | 'expirado';
export type StatusDoSignatario = 'pendente' | 'assinado' | 'recusado';

export const STATUS_DO_DOCUMENTO: readonly StatusDoDocumento[] = ['pendente', 'assinado', 'recusado', 'cancelado', 'expirado'];

export const ROTULO_DO_DOCUMENTO: Record<StatusDoDocumento, string> = {
  pendente: 'Aguardando assinatura',
  assinado: 'Assinado',
  recusado: 'Recusado',
  cancelado: 'Cancelado',
  expirado: 'Expirado',
};

export const COR_DO_DOCUMENTO: Record<StatusDoDocumento, 'warning' | 'success' | 'error' | 'default'> = {
  pendente: 'warning',
  assinado: 'success',
  recusado: 'error',
  cancelado: 'default',
  expirado: 'default',
};

export const ROTULO_DO_SIGNATARIO: Record<StatusDoSignatario, string> = {
  pendente: 'Pendente',
  assinado: 'Assinou',
  recusado: 'Recusou',
};

export const COR_DO_SIGNATARIO: Record<StatusDoSignatario, 'warning' | 'success' | 'error'> = {
  pendente: 'warning',
  assinado: 'success',
  recusado: 'error',
};

export interface Signatario {
  id: number;
  nome: string;
  cpf: string;
  whatsapp: string | null;
  email: string | null;
  employeeId: number | null;
  status: StatusDoSignatario;
  visualizadoEm: string | null;
  cpfConfirmadoEm: string | null;
  assinadoEm: string | null;
  recusadoEm: string | null;
  motivoRecusa: string | null;
  otpCanal: string | null;
  ip: string | null;
  userAgent: string | null;
}

export interface DocumentoParaAssinatura {
  id: number;
  companyId: number;
  origemTipo: string;
  origemId: number;
  titulo: string;
  status: StatusDoDocumento;
  hashOriginal: string;
  hashFinal: string | null;
  codigoVerificacao: string;
  expiraEm: string;
  concluidoEm: string | null;
  createdAt: string;
  signatarios: Signatario[];
}

export type TipoDeEvento =
  | 'criado'
  | 'enviado'
  | 'envio_falhou'
  | 'reenviado'
  | 'visualizado'
  | 'cpf_confirmado'
  | 'cpf_invalido'
  | 'codigo_enviado'
  | 'codigo_invalido'
  | 'assinado'
  | 'recusado'
  | 'lacrado'
  | 'cancelado'
  | 'expirado'
  | 'baixado';

export const ROTULO_DO_EVENTO: Record<TipoDeEvento, string> = {
  criado: 'Documento criado',
  enviado: 'Link enviado',
  envio_falhou: 'Envio do link falhou',
  reenviado: 'Link reenviado',
  visualizado: 'Documento aberto',
  cpf_confirmado: 'CPF confirmado',
  cpf_invalido: 'CPF não conferiu',
  codigo_enviado: 'Código enviado',
  codigo_invalido: 'Código incorreto',
  assinado: 'Assinou',
  recusado: 'Recusou',
  lacrado: 'Documento lacrado',
  cancelado: 'Cancelado',
  expirado: 'Prazo vencido',
  baixado: 'Arquivo baixado',
};

export interface EventoDeAssinatura {
  id: number;
  signatarioId: number | null;
  tipo: TipoDeEvento;
  ip: string | null;
  userAgent: string | null;
  detalhes: Record<string, unknown> | null;
  ocorreuEm: string;
}

export interface ListaDeDocumentos {
  documentos: DocumentoParaAssinatura[];
  total: number;
}

export interface DetalheDoDocumento {
  documento: DocumentoParaAssinatura;
  eventos: EventoDeAssinatura[];
}

// ── Frente pública ──

export interface ResumoPublico {
  titulo: string;
  empresa: string;
  signatario: { nome: string; cpfMascarado: string };
  status: StatusDoSignatario;
  documentoStatus: StatusDoDocumento;
  expiraEm: string;
  cpfConfirmado: boolean;
  codigoEnviadoPara: string | null;
  assinadoDisponivel: boolean;
  codigoDeVerificacao: string | null;
}

export interface CodigoEnviado {
  canal: string;
  destino: string;
  expiraEmMinutos: number;
}

export interface Verificacao {
  titulo: string;
  empresa: string;
  status: StatusDoDocumento;
  criadoEm: string;
  concluidoEm: string | null;
  hashOriginal: string;
  hashFinal: string | null;
  codigoDeVerificacao: string;
  signatarios: { nome: string; status: StatusDoSignatario; assinadoEm: string | null }[];
}

/** De onde o documento veio — o `origemTipo` que cada consumidor do motor grava. */
export const ROTULO_DA_ORIGEM: Record<string, string> = {
  epi_entrega: 'Entrega de EPI',
  aso: 'ASO',
  ordem_de_servico: 'Ordem de Serviço',
};

export function rotuloDaOrigem(origemTipo: string): string {
  return ROTULO_DA_ORIGEM[origemTipo] ?? origemTipo;
}
